import { Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { ActivatedRoute } from "@angular/router";
import { forkJoin, Observable, of } from "rxjs";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, map, switchMap } from "rxjs/operators";
import {
  AppBarTitleService,
  CustomerLoanStatus,
  DocumentFilters,
  DocumentQueryParams,
  EntityDocumentsService,
  FilterParameter,
  LoanDocumentService,
  LoanFileTemplateData,
  MessageService,
  QueryParamsService,
} from "common";
import { EntityDocumentsListData } from "./models/entity-documents-list-data.model";
import { DocumentListFilterDialogComponent } from "../../../document/document-list-filter-dialog/document-list-filter-dialog.component";
import { StatementLoanOption } from "../../../document/request-statement-dialog/models/request-statement-dialog-data.model";
import { RequestStatementDialogComponent } from "../../../document/request-statement-dialog/request-statement-dialog.component";
import { LoanFacade } from "../../../loan/domain/+state/loan.facade";
import { LoanData, LoanDataSummary } from "../../../loan/domain/models/loan.model";
import { EntityDocumentsFilterService } from "../../entity-documents-filter.service";

// Must equal the server's StatementTemplateGroup — templates are matched by group, not name.
const STATEMENT_TEMPLATE_GROUP = "TRX_HIST_STAT";
const NO_STATEMENTS_MESSAGE = "No statements are available for your loans yet.";

@UntilDestroy()
@Component({
    selector: "ifp-entity-documents-list",
    templateUrl: "./entity-documents-list.component.html",
    styleUrls: ["./entity-documents-list.component.scss"],
    standalone: false,
})
export class EntityDocumentsListComponent implements OnInit {
  tableColumns: string[] = ["createdOn", "name", "category", "download"];

  filters: DocumentFilters;
  activeFilters: FilterParameter[] = [];

  data: EntityDocumentsListData;
  queryParams: DocumentQueryParams;
  requesting = false;

  @ViewChild(MatPaginator, { static: true })
  paginator: MatPaginator;

  @ViewChild(MatSort)
  sort: MatSort;

  constructor(
    private appBarTitleService: AppBarTitleService,
    private route: ActivatedRoute,
    private queryParamsService: QueryParamsService,
    private entityDocumentsFilterService: EntityDocumentsFilterService,
    private entityDocumentsService: EntityDocumentsService,
    private dialog: MatDialog,
    private loanFacade: LoanFacade,
    private loanDocumentService: LoanDocumentService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data: EntityDocumentsListData) => {
        this.data = data;
        this.appBarTitleService.title = `Documents - ${data?.entity?.name}`;
      });
    this.route.queryParams
      .pipe(untilDestroyed(this))
      .subscribe((params) => {
        this.queryParams = new DocumentQueryParams(this.queryParamsService.init(params));
        this.activeFilters = this.entityDocumentsFilterService.addFilterParameters(
          this.queryParams
        );
      });

    this.filters = new DocumentFilters(this.queryParams);
    this.paginator.pageSize = this.queryParams.limit;
    this.paginator.pageIndex = this.queryParams.skip / this.queryParams.limit;
  }

  activeFilterRemoved(activefilters: FilterParameter[]): void {
    if (!activefilters.length) {
      return;
    }

    activefilters.forEach((item) => {
      this.filters.remove(item.id);
      this.entityDocumentsFilterService.filter(this.filters);
    });
  }

  filter(): void {
    this.entityDocumentsFilterService.filter(
      this.filters,
      this.paginator,
      this.sort
    );
  }

  openFilterDialog(): void {
    DocumentListFilterDialogComponent.show(this.dialog, new DocumentFilters(this.queryParams), this.data.entityDocumentsQueryResult.values)
    .subscribe(result => {
      if (result) {
        this.filters = result;
        this.paginator.firstPage();
        this.filter();
      }
    });
  }

  // Resolve the entity's eligible loans (active + statement-capable) up front, then open the picker dialog —
  // or tell the customer nothing is available yet. The dropdown never lists a loan that can't produce one.
  requestStatement(): void {
    if (this.requesting)
      return;
    this.requesting = true;
    this.loanFacade
      .getSummaryByEntity(this.data.entity.id)
      .pipe(
        switchMap((summary: LoanDataSummary) => this.resolveEligibleLoans(summary)),
        catchError(() => of<StatementLoanOption[]>([])),
        untilDestroyed(this)
      )
      .subscribe((loans: StatementLoanOption[]) => {
        this.requesting = false;
        if (!loans.length) {
          this.messageService.info(NO_STATEMENTS_MESSAGE);
          return;
        }
        RequestStatementDialogComponent.show(this.dialog, { loans })
          .pipe(untilDestroyed(this))
          .subscribe();
      });
  }

  onClickDownload(fileId: number): void {
    this.entityDocumentsService.download(fileId);
  }

  // LOC/Term only (case financing is a separate array); a statement template must resolve per loan. Each
  // per-loan lookup fails closed (catchError -> []) so one bad lookup just drops that loan from the list.
  private resolveEligibleLoans(summary: LoanDataSummary): Observable<StatementLoanOption[]> {
    const activeLoans = (summary?.otherLoans ?? []).filter((loan) => this.isActive(loan));
    if (!activeLoans.length)
      return of([]);

    return forkJoin(
      activeLoans.map((loan) =>
        this.loanDocumentService.getLoanFileTemplates(loan.id).pipe(
          catchError(() => of<LoanFileTemplateData[]>([])),
          map((templates) => this.toStatementOption(loan, templates))
        )
      )
    ).pipe(map((options) => options.filter((option): option is StatementLoanOption => option != null)));
  }

  private isActive(loan: LoanData): boolean {
    return loan.customerFacingStatus !== CustomerLoanStatus.Closed
      && loan.customerFacingStatus !== CustomerLoanStatus.PendingClosure
      && loan.customerFacingStatus !== CustomerLoanStatus.Renewed;
  }

  private toStatementOption(loan: LoanData, templates: LoanFileTemplateData[]): StatementLoanOption | null {
    const template = templates.find((t) => t.group?.toUpperCase() === STATEMENT_TEMPLATE_GROUP);
    if (template == null)
      return null;
    return {
      loanId: loan.id,
      templateId: template.id,
      productCode: loan.productCode,
      label: loan.productName,
      lastFour: this.lastFour(loan.loanNumber),
      // The summary doesn't carry chainOriginatedOn; the dialog fetches the loan on selection to floor the picker.
      accountOpenedOn: loan.chainOriginatedOn,
    };
  }

  private lastFour(loanNumber?: string): string {
    return loanNumber != null && loanNumber.length > 4 ? loanNumber.slice(-4) : (loanNumber ?? "");
  }
}
